import { useCallback, useEffect, useState } from 'react';

export type Theme = 'light' | 'dark';
export type ThemePreference = Theme | 'system';

const STORAGE_KEY = 'carrierbridge:theme';

const media =
  typeof window !== 'undefined' && window.matchMedia
    ? window.matchMedia('(prefers-color-scheme: dark)')
    : null;

function readStoredPreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === 'dark' || stored === 'light' ? stored : 'system';
  } catch {
    // localStorage indisponible (SSR, mode privé restrictif…) → suit l'OS.
    return 'system';
  }
}

function resolve(preference: ThemePreference): Theme {
  if (preference === 'system') return media?.matches ? 'dark' : 'light';
  return preference;
}

function applyTheme(theme: Theme) {
  document.documentElement.setAttribute('data-theme', theme);
}

// Applique le thème persisté dès le chargement du module (avant le premier
// rendu React) pour éviter le flash clair au démarrage. Défaut : 'system' (suit l'OS).
applyTheme(resolve(readStoredPreference()));

export function useTheme(): {
  /** Thème effectivement appliqué (résolu depuis l'OS en mode 'system'). */
  theme: Theme;
  /** Choix de l'utilisateur : light, dark ou system. */
  preference: ThemePreference;
  /** Cycle light → dark → system. */
  toggle: () => void;
} {
  const [preference, setPreference] = useState<ThemePreference>(readStoredPreference);
  const [theme, setTheme] = useState<Theme>(() => resolve(readStoredPreference()));

  // En mode 'system', suit les changements de préférence OS en direct.
  useEffect(() => {
    if (preference !== 'system' || !media) return;
    const onChange = () => {
      const next = resolve('system');
      applyTheme(next);
      setTheme(next);
    };
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [preference]);

  const toggle = useCallback(() => {
    setPreference((prev) => {
      const next: ThemePreference =
        prev === 'light' ? 'dark' : prev === 'dark' ? 'system' : 'light';
      try {
        localStorage.setItem(STORAGE_KEY, next);
      } catch {
        // Pas de persistance possible : le thème reste appliqué pour la session.
      }
      const resolved = resolve(next);
      applyTheme(resolved);
      setTheme(resolved);
      return next;
    });
  }, []);

  return { theme, preference, toggle };
}
